// https://www.w3.org/WAI/ARIA/apg/example-index/breadcrumb/index.html
import React, {
	Children,
	cloneElement,
	Fragment,
	isValidElement,
	useEffect,
	useRef,
} from 'react';
import clsx from 'clsx';
import {isFragment} from 'react-is';
import ResizeDetector from 'react-resize-detector';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {combineRefs} from '@netx/core-next/utils/react';
import {
	BREADCRUMBS_CLASS_NAME,
	breadcrumbsClasses,
	BreadcrumbsClasses,
	getBreadcrumbsUtilityClass,
} from './breadcrumbsClasses';
import {BreadcrumbSeparator} from './BreadcrumbSeparator';
import {BreadcrumbTruncatedMenu} from './BreadcrumbsTruncatedMenu';
import {useBreadcrumbsOverflow} from './useBreadcrumbsOverflow';

export type BreadcrumbsElement = HTMLDivElement;
export type BreadcrumbsRef = React.ForwardedRef<BreadcrumbsElement>;

type UtilityClassProps = WithClasses<BreadcrumbsClasses, {}>;

const useUtilityClasses = (ownerState: UtilityClassProps) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
		list: ['list'],
	};
	return composeClasses(slots, getBreadcrumbsUtilityClass, classes);
};

const BreadcrumbsRoot = styled('nav', {
	name: BREADCRUMBS_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	alignItems: 'center',
	flex: '0 0 auto',
	minHeight: 36,
	overflow: 'hidden',
}));

const BreadcrumbsList = styled('ol', {
	name: BREADCRUMBS_CLASS_NAME,
	slot: 'List',
})(({theme}) => ({
	alignItems: 'center',
	display: 'flex',
	margin: 0,
	paddingLeft: 0,
	listStyle: 'none',
	overflow: 'hidden',
}));

export interface BreadcrumbsProps extends WithClasses<BreadcrumbsClasses> {
	children?: React.ReactNode;
	/** minimum items to hold onto post truncation */
	itemsAfterCollapse?: number;
	/** minimum items to hold onto pre truncation */
	itemsBeforeCollapse?: number;
}

const BreadcrumbsInner = ({
	classes: _classes,
	className,
	children: _children,
	itemsBeforeCollapse = 1,
	itemsAfterCollapse = 1,
}: BreadcrumbsProps) => {
	const rootRef = useRef<BreadcrumbsElement>(null);
	const crumbsRef = useRef<HTMLOListElement>(null);

	const ownerState: UtilityClassProps = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	const {onResize, overflown, placeIndexesInMenu} = useBreadcrumbsOverflow({
		rootRef,
		crumbsRef,
		itemsBeforeCollapse,
		itemsAfterCollapse,
	});

	// when children passed in change, update the overlow menu
	useEffect(() => {
		onResize();
	}, [_children]);

	// convert to flat array of valid children
	const childrenArray = Children.toArray(_children)
		.filter((child) => isValidElement(child))
		.map((child) => {
			if (isFragment(child)) return Children.toArray(child.props.children);
			return child;
		})
		.flat() as React.ReactChild[];

	const totalChildren = childrenArray.length;

	const children = childrenArray.map((child, index) => {
		if (!isValidElement(child)) return null;

		const hidden = placeIndexesInMenu.includes(index);
		return (
			<Fragment key={index}>
				{cloneElement(child, {
					...(child as any).props,
					style: {
						// hide if truncated
						display: hidden ? 'none' : undefined,
						// set last item to grow/shrink as needed for ellipsis text overflow
						flex: index === totalChildren - 1 ? '1 1 auto' : undefined,
						...(child.props as any)?.styles,
					},
				})}
				{/** show separator if crumb was not hidden and not the last crumb in the list */}
				{!hidden && index < totalChildren - 1 ? <BreadcrumbSeparator /> : null}
				{/** show overflow menu, in the right position, if we have overflown */}
				{overflown &&
				index > itemsBeforeCollapse - 1 &&
				index < itemsBeforeCollapse + 1 ? (
					<>
						<BreadcrumbTruncatedMenu
							children={childrenArray.filter((_, index) =>
								placeIndexesInMenu.includes(index),
							)}
						/>
						<BreadcrumbSeparator />
					</>
				) : null}
			</Fragment>
		);
	});

	return (
		<ResizeDetector<HTMLDivElement> handleWidth onResize={onResize}>
			{({targetRef}) => (
				<BreadcrumbsRoot
					aria-label="breadcrumb"
					className={clsx(classes.root, className)}
					ref={combineRefs(rootRef, targetRef)}
				>
					<BreadcrumbsList ref={crumbsRef} className={classes.list}>
						{children}
					</BreadcrumbsList>
				</BreadcrumbsRoot>
			)}
		</ResizeDetector>
	);
};

export const BreadcrumbsPrimitive = BreadcrumbsInner;

export const Breadcrumbs = BreadcrumbsPrimitive;

export default Breadcrumbs;
